import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { Box, Chip, chipClasses, LinearProgress, linearProgressClasses, TableCell, tableCellClasses, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Country } from 'src/api/zrm';
import { formatClock, formatCount } from 'src/tv/dashboardFormat';
import type { GoalView } from 'src/tv/dashboardView';
import { TV_COLORS, TV_PODIUM } from 'src/tv/tvTheme';
import { getLocale } from 'src/utils/getLocale';

import type { SxProps, Theme } from '@mui/material';

/**
 * Building blocks shared by the MB and KH screens. Everything is sized in vw/vh (see TvFrame) and
 * every number is tabular, so columns of figures line up and do not jitter between refreshes.
 */

export const NUMERIC = { fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' } as const;

/** Section heading: the one kicker on the screen, one per column or table. */
export const Kicker: FC<{ children: ReactNode }> = (props) => {
  const { children } = props;

  return (
    <Typography sx={{ fontSize: '1.1vw', fontWeight: 600, color: TV_COLORS.muted, lineHeight: 1.2 }}>
      {children}
    </Typography>
  );
};

interface GoalHeadlineProps {
  label: string;
  view: GoalView;
  caption?: string;
}

/** A result in big numerals against its goal, with a slim meter that turns green once it is reached. */
export const GoalHeadline: FC<GoalHeadlineProps> = (props) => {
  const { label, view, caption } = props;
  const { t } = useTranslation();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '1vw' }}>
        <Kicker>{label}</Kicker>
        {caption && (
          <Typography sx={{ ...NUMERIC, fontSize: '0.95vw', color: TV_COLORS.muted, whiteSpace: 'nowrap' }}>
            {caption}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '0.8vw', mt: '0.6vh' }}>
        <Typography
          sx={{ ...NUMERIC, fontSize: '4.2vw', fontWeight: 700, lineHeight: 1, color: view.isMet ? TV_COLORS.win : TV_COLORS.text }}
        >
          {formatCount(view.result)}
        </Typography>
        <Typography sx={{ ...NUMERIC, fontSize: '1.8vw', fontWeight: 500, color: TV_COLORS.muted, whiteSpace: 'nowrap' }}>
          {t('goal {{goal}}', { goal: formatCount(view.goal) })}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={(view.progress ?? 0) * 100}
        aria-valuenow={Math.round((view.progress ?? 0) * 100)}
        aria-label={label}
        sx={{
          mt: '1.2vh',
          height: '0.7vh',
          borderRadius: '0.35vh',
          bgcolor: TV_COLORS.line,
          [`& .${linearProgressClasses.bar}`]: {
            bgcolor: view.isMet ? TV_COLORS.win : TV_COLORS.accent,
            transition: 'transform 600ms cubic-bezier(0.16, 1, 0.3, 1)',
          },
        }}
      />
    </Box>
  );
};

interface FigureProps {
  label: string;
  value: string;
  detail?: string;
}

/** A count with an optional volume under it, for the "today" and totals rows. */
export const Figure: FC<FigureProps> = (props) => {
  const { label, value, detail } = props;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '1vw', fontWeight: 600, color: TV_COLORS.muted, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      <Typography sx={{ ...NUMERIC, fontSize: '2.8vw', fontWeight: 700, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
      {detail && (
        <Typography sx={{ ...NUMERIC, fontSize: '1.3vw', fontWeight: 500, color: TV_COLORS.text, opacity: 0.8, whiteSpace: 'nowrap' }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
};

interface RankChipProps {
  rank: number;
  /** The podium steps; ranks beyond them get a plain number. */
  podium?: typeof TV_PODIUM;
}

/** Rank number; the top ranks sit on the green podium ramp. */
export const RankChip: FC<RankChipProps> = (props) => {
  const { rank, podium: steps = TV_PODIUM } = props;
  const podium = steps[rank - 1];

  return (
    <Chip
      label={rank}
      size="small"
      sx={{
        ...NUMERIC,
        minWidth: '1.45em',
        height: '1.45em',
        borderRadius: '0.3em',
        fontSize: 'inherit',
        fontWeight: 700,
        verticalAlign: 'baseline',
        bgcolor: podium?.fill ?? 'transparent',
        color: podium?.text ?? TV_COLORS.muted,
        [`& .${chipClasses.label}`]: { px: '0.2em', overflow: 'visible' },
      }}
    />
  );
};

/**
 * The leaderboards are MUI tables made dense for a wall screen: no cell padding or rules of MUI's
 * own, the table's font size for every cell, hairlines between rows. Zeros are dimmed: on a wall
 * screen they are noise.
 */
export const tableSx = (fontSize: string): SxProps<Theme> => ({
  height: '100%',
  fontSize,
  [`& .${tableCellClasses.root}`]: {
    px: '0.55vw',
    py: 0,
    border: 'none',
    fontSize: 'inherit',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    textAlign: 'right',
    color: TV_COLORS.text,
    ...NUMERIC,
  },
  [`& .${tableCellClasses.head}`]: { fontSize: '0.8em', fontWeight: 600, color: TV_COLORS.muted, verticalAlign: 'bottom', pb: '0.6vh' },
  [`& thead tr:first-of-type .${tableCellClasses.head}`]: { pb: '0.2vh', fontSize: '0.85em', color: TV_COLORS.text, textAlign: 'left' },
  [`& .${tableCellClasses.body}`]: { borderTop: `1px solid ${TV_COLORS.line}` },
  [`& .${tableCellClasses.footer}`]: { borderTop: `2px solid ${TV_COLORS.muted}`, fontWeight: 700, pt: '0.4vh' },
  // Colour only, no opacity, so the podium wash behind the cell stays even.
  '& td[data-zero="true"]': { color: 'rgba(147, 167, 188, 0.5)' },
  '& .name': { textAlign: 'left', fontWeight: 600, maxWidth: '13vw', overflow: 'hidden', textOverflow: 'ellipsis' },
  '& .rank': { textAlign: 'left', width: '2.6vw', pr: 0 },
  '& .strong': { fontWeight: 700 },
  // A group starts with a little air and a hairline, so Today and the month read as two tables.
  '& .group-start': { pl: '1.6vw', borderLeft: `1px solid ${TV_COLORS.line}` },
  '& tr.podium td': { bgcolor: 'rgba(70, 215, 138, 0.07)' },
  '& tr.podium-end td': { borderBottom: `2px solid ${TV_COLORS.muted}` },
});

/** Row class for the leaderboards: a faint green wash on the podium, a rule under its last step. */
export const podiumRowClass = (index: number, podiumSize: number, rows: number): string | undefined => {
  if (index >= podiumSize) return undefined;

  return index === podiumSize - 1 && rows > podiumSize ? 'podium podium-end' : 'podium';
};

/** A number cell; `data-zero` lets the table dim it. */
export const NumCell: FC<{ value: number; text: string; className?: string }> = (props) => {
  const { value, text, className } = props;

  return (
    <TableCell
      className={className}
      data-zero={value === 0}
    >
      {text}
    </TableCell>
  );
};

export const monthName = (date: string, country: Country): string => {
  const name = new Date(`${date}T12:00:00Z`).toLocaleDateString(getLocale(country), { month: 'long', timeZone: 'UTC' });

  return name.charAt(0).toUpperCase() + name.slice(1);
};

/** A snapshot this old means zrm's cron stopped; the screen says so rather than look current. */
const STALE_AFTER_MS = 5 * 60_000;

interface DashboardStatusProps {
  country: Country;
  /** The dashboard's zone; until the first snapshot names one, the country's (never the TV's own, often UTC). */
  timezone?: string;
  generatedAt: string | null;
  offlineSince: number | null;
}

/** Stockholm and Oslo share a zone, so a screen that does not know its country yet still shows the office time. */
export const COUNTRY_TIMEZONES: Record<Country, string> = {
  [Country.SE]: 'Europe/Stockholm',
  [Country.NO]: 'Europe/Oslo',
};

/**
 * Top-right corner: the clock, and under it how fresh the numbers are. The one-second tick lives here,
 * so only this corner re-renders every second, not the dashboard under it.
 */
export const DashboardStatus: FC<DashboardStatusProps> = (props) => {
  const { country, timezone: snapshotTimezone, generatedAt, offlineSince } = props;
  const { t } = useTranslation();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const tick = window.setInterval(() => setNow(new Date()), 1_000);

    return () => window.clearInterval(tick);
  }, []);

  const timezone = snapshotTimezone ?? COUNTRY_TIMEZONES[country];

  const clock = formatClock(now.toISOString(), timezone, country);
  const isStale = generatedAt !== null && now.getTime() - new Date(generatedAt).getTime() > STALE_AFTER_MS;

  let status: string | null = null;

  if (offlineSince !== null) {
    const since = formatClock(new Date(offlineSince).toISOString(), timezone, country);

    status = generatedAt
      ? t('Offline since {{time}} · numbers from {{updated}}', { time: since, updated: formatClock(generatedAt, timezone, country) })
      : t('Offline since {{time}}', { time: since });
  } else if (generatedAt)
    status = t('Updated {{time}}', { time: formatClock(generatedAt, timezone, country) });

  return (
    <Box sx={{ textAlign: 'right' }}>
      <Typography sx={{ ...NUMERIC, fontSize: '1.9vw', fontWeight: 600, lineHeight: 1 }}>
        {clock}
      </Typography>
      {status && (
        <Typography
          data-testid="tv-dashboard-status"
          sx={{
            ...NUMERIC,
            mt: '0.4vh',
            fontSize: '0.9vw',
            color: offlineSince !== null || isStale ? TV_COLORS.warning : TV_COLORS.muted,
          }}
        >
          {status}
        </Typography>
      )}
    </Box>
  );
};
